import React, { useMemo } from 'react';
import { ChevronRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { NavigationItem as NavItemType } from '../types/navigation';
import TeamNavigationItem from './TeamNavigationItem';
import TeammateNavigationItem from './TeammateNavigationItem';
import { Badge } from '@/components/ui';
import BotInboxesNavigationItem from './BotInboxesNavigationItem';
import TeammateScorecardNavigationItem from './TeammateScorecardNavigationItem';

interface NavigationItemProps {
  item: NavItemType;
  badge?: number;
  isSecondPanelCollapsed: boolean;
  expandedItems: string[];
  toggleExpanded: (itemTitle: string) => void;
  hasActiveChild?: (children: NavItemType[]) => boolean;
  isItemActive: (path: string) => boolean;
  navigate: (path: string) => void;
  filterMenuItems: (items: NavItemType[]) => NavItemType[];
  hasNotification?: boolean;
}

const NavigationItem = ({
  item,
  badge = 0,
  isSecondPanelCollapsed,
  expandedItems,
  toggleExpanded,
  hasActiveChild,
  isItemActive,
  navigate,
  filterMenuItems,
  hasNotification = false,
}: NavigationItemProps) => {
  // Parent items with children should toggle expansion instead of navigating

  const renderBadge = () => {
    if (hasNotification) {
      return <span className="absolute -right-[1px] h-2 w-2 rounded-full bg-red-500" />;
    }
    if (badge <= 0) return null;
    // collapsed = tiny dot; expanded = number badge
    return isSecondPanelCollapsed ? (
      <span className="absolute -right-[1px] h-2 w-2 rounded-full bg-primary" />
    ) : (
      <Badge
        variant="default"
        className="absolute right-1 h-5 w-5 flex items-center justify-center p-0 text-[10px]"
      >
        {badge > 99 ? '99+' : badge}
      </Badge>
    );
  };
  // For Teams item with loadDynamicChildren flag, use the TeamNavigationItem
  if (item.loadDynamicChildren && item.title === "Teams") {
    return (
      <TeamNavigationItem
        item={item}
        badge={badge}
        isSecondPanelCollapsed={isSecondPanelCollapsed}
        expandedItems={expandedItems}
        toggleExpanded={toggleExpanded}
        hasActiveChild={hasActiveChild}
        isItemActive={isItemActive}
        navigate={navigate}
        filterMenuItems={filterMenuItems}
      />
    );
  }

  // For Teammates item with loadDynamicChildren flag, use the TeammateNavigationItem
  if (item.loadDynamicChildren && item.title === "Teammates") {
    return (
      <TeammateNavigationItem
        item={item}
        badge={badge}
        isSecondPanelCollapsed={isSecondPanelCollapsed}
        expandedItems={expandedItems}
        toggleExpanded={toggleExpanded}
        hasActiveChild={hasActiveChild}
        isItemActive={isItemActive}
        navigate={navigate}
        filterMenuItems={filterMenuItems}
      />
    );
  }

  // For Bot Inboxes item with loadDynamicChildren flag, use the BotInboxesNavigationItem
  if (item.loadDynamicChildren && item.title === "Bot Inboxes") {
    return (
      <BotInboxesNavigationItem
        item={item}
        isSecondPanelCollapsed={isSecondPanelCollapsed}
        expandedItems={expandedItems}
        toggleExpanded={toggleExpanded}
        hasActiveChild={hasActiveChild}
        isItemActive={isItemActive}
        navigate={navigate}
        filterMenuItems={filterMenuItems}
      />
    );
  }

  // For Teammate Scorecards item with loadDynamicChildren flag, use the TeammateScorecardNavigationItem
  if (item.loadDynamicChildren && item.title === "Teammate Scorecards") {
    return (
      <TeammateScorecardNavigationItem
        item={item}
        isSecondPanelCollapsed={isSecondPanelCollapsed}
        expandedItems={expandedItems}
        toggleExpanded={toggleExpanded}
        hasActiveChild={hasActiveChild}
        isItemActive={isItemActive}
        navigate={navigate}
        filterMenuItems={filterMenuItems}
      />
    );
  }

  if (item.children) {
    return (
      <div>
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                variant="ghost"
                className={`w-full flex items-center rounded-lg transition-all duration-300 group ${isSecondPanelCollapsed ? 'justify-center p-2' : 'justify-between px-4 py-2'
                  } ${hasActiveChild?.(item.children)
                    ? 'bg-primary/5 text-primary shadow-sm hover:shadow-md'
                    : 'hover:bg-primary/5'}`}
                onClick={() => toggleExpanded(item.title)}
              >
                <div className={`flex items-center ${isSecondPanelCollapsed ? 'justify-center' : 'gap-3'}`}>
                  {item.icon && (
                    <div className="flex items-center justify-center w-5">
                      <item.icon className="h-4 w-4 transition-transform duration-300 group-hover:scale-110" />
                    </div>
                  )}
                  {!isSecondPanelCollapsed && (
                    <span className="font-medium transition-colors group-hover:text-primary">
                      {item.title}
                    </span>
                  )}
                </div>
                {renderBadge()}
                {!isSecondPanelCollapsed && (
                  <ChevronRight className={`h-4 w-4 transition-all duration-300 ${expandedItems.includes(item.title) ? 'rotate-90' : ''
                    } group-hover:text-primary`} />
                )}
              </Button>
            </TooltipTrigger>
            <TooltipContent
              side="right"
              className="z-[60] bg-white shadow-lg"
              sideOffset={12}
            >
              <p>{item.title}</p>
              <p className="text-xs text-muted-foreground">
                Click to {expandedItems.includes(item.title) ? 'collapse' : 'expand'}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        {expandedItems.includes(item.title) && !isSecondPanelCollapsed && (
          <div className="ml-8 mt-1 space-y-1 animate-accordion-down">
            {filterMenuItems(item.children).map((child: NavItemType) => (
              <TooltipProvider key={child.title} delayDuration={0}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      className={`w-full flex items-center justify-start text-sm px-4 py-2 rounded-lg transition-all duration-300 group
                        ${isItemActive(child.path)
                          ? 'bg-primary/5 text-primary shadow-sm hover:shadow-md'
                          : 'hover:bg-primary/5'}`}
                      onClick={() => navigate(child.path)}
                    >
                      <span className="transition-colors group-hover:text-primary">
                        {child.title}
                      </span>
                      {renderBadge()}
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent
                    side="right"
                    className="z-[60] bg-white shadow-lg"
                    sideOffset={12}
                  >
                    <p>{child.title}</p>
                    <p className="text-xs text-muted-foreground">Click to navigate</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ))}
          </div>
        )}
      </div>
    );
  }

  // Leaf items (without children) should navigate directly
  return (
    <TooltipProvider delayDuration={0}>
      <Tooltip>
        <TooltipTrigger asChild>
          <Button
            variant="ghost"
            className={`w-full flex items-center rounded-lg transition-all duration-300 group ${isSecondPanelCollapsed ? 'justify-center p-2' : 'justify-start px-4 py-2'
              } ${isItemActive(item.path)
                ? 'bg-primary/5 text-primary shadow-sm hover:shadow-md'
                : 'hover:bg-primary/5'}`}
            onClick={() => navigate(item.path)}
          >
            <div className={`flex items-center ${isSecondPanelCollapsed ? 'justify-center' : 'gap-3'}`}>
              {item.icon && (
                <div className="flex items-center justify-center w-5">
                  <item.icon className="h-4 w-4 transition-transform duration-300 group-hover:scale-110" />
                </div>
              )}
              {!isSecondPanelCollapsed && (
                <span className="font-medium transition-colors group-hover:text-primary">
                  {item.title}
                </span>
              )}
            </div>
            {renderBadge()}
          </Button>
        </TooltipTrigger>
        <TooltipContent
          side="right"
          className="z-[60] bg-white shadow-lg"
          sideOffset={12}
        >
          <p>{item.title}</p>
          <p className="text-xs text-muted-foreground">Click to navigate</p>
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export default NavigationItem;
